/**
 * Helpers shared by the chat store modules (clientsChat.js, chat.js).
 *
 * Chat state is fed from two independent sources — Centrifuge publications and
 * /conversation/message/list responses — so it can never be a plain assignment:
 * a response built before a burst of publications would otherwise wipe them.
 */

// Presentation-only fields some components add to a message before handing it back.
const CLIENT_ONLY_KEYS = ['sendAtTime', 'sendAtDate'];

/**
 * Strip presentation-only fields so they never end up in store state.
 * @param {Object} message
 * @returns {Object}
 */
export const sanitizeMessage = (message) => {
  const copy = {...message};
  CLIENT_ONLY_KEYS.forEach((key) => delete copy[key]);
  return copy;
};

/**
 * Messages carry a monotonic per-conversation sequence `number`, which is the only
 * reliable ordering key — publications can arrive out of order.
 */
export const byNumberAsc = (a, b) => (a.number ?? 0) - (b.number ?? 0);

/**
 * Additive merge of a server page into local state. Never removes a message, so a
 * response can never make a visible message disappear regardless of arrival order.
 * Deletions keep flowing through the explicit delete mutations.
 *
 * @param {Array} existing - current message array
 * @param {Array} incoming - messages from /conversation/message/list
 * @param {Object} options
 * @param {boolean} options.fresh - true when this is the newest response applied so far.
 *   A superseded (stale) response may only insert messages that are missing; it may not
 *   overwrite fields, which is what stops an older refresh reverting a newer one.
 * @returns {Array} merged, ordered messages
 */
export const mergeMessages = (existing, incoming, {fresh = true} = {}) => {
  const byId = new Map(existing.map((message) => [message.messageId, message]));
  (incoming || []).forEach((raw) => {
    const next = sanitizeMessage(raw);
    const prev = byId.get(next.messageId);
    if (!prev) {
      byId.set(next.messageId, next);
      return;
    }
    if (!fresh) return;
    byId.set(next.messageId, {
      ...prev, // keep fields the list endpoint may omit
      ...next, // server copy wins, so edits and status changes apply
      // Read flags are monotonic: a refresh that raced a mark-read must not un-read.
      supportIsRead: next.supportIsRead || prev.supportIsRead,
      clientIsRead: next.clientIsRead || prev.clientIsRead,
    });
  });
  return [...byId.values()].sort(byNumberAsc);
};

/**
 * Insert a message received over the websocket, ignoring a re-publication and
 * keeping the array ordered by `number`.
 * @param {Array} existing
 * @param {Object} message
 * @returns {Array}
 */
export const insertMessage = (existing, message) => {
  if (!message || existing.some((item) => item.messageId === message.messageId)) return existing;
  return [...existing, sanitizeMessage(message)].sort(byNumberAsc);
};
